// PuckView3D — the hero puck: emissive gold cylinder, the scene's only
// dynamic light parented to it, an additive motion streak, and a blob
// shadow that scales with Shot-mode lift.

import { type MatchSnapshot, tuning } from "@arcade-hockey/sim";
import { Group, Mesh, PointLight } from "three/webgpu";
import {
  buildDecalQuadGeometry,
  buildPuckGeometry,
  buildTrailGeometry,
} from "../geometry/entity-geometry.js";
import { type PuckUniforms, applyPuckStyle, createPuckMaterials } from "../materials/puck.js";
import { type EntitiesStyle, entitiesStyle } from "../style/style-schema.js";
import { simToWorldInto } from "../world-mapping.js";

type PuckModule = typeof import("../materials/puck.js");

export class PuckView3D {
  readonly root: Group;
  private readonly body: Mesh;
  private readonly streak: Mesh;
  private readonly shadow: Mesh;
  private readonly light: PointLight;
  private u: PuckUniforms;
  private prevX = 0;
  private prevY = 0;
  private hasPrev = false;

  constructor() {
    this.root = new Group();
    const mats = createPuckMaterials(entitiesStyle);
    this.u = mats.u;
    const r = tuning.puck.radius;

    this.body = new Mesh(buildPuckGeometry(), mats.body);
    this.body.scale.setScalar(r);
    this.root.add(this.body);

    this.streak = new Mesh(buildTrailGeometry(), mats.streak);
    this.streak.renderOrder = 2;
    this.root.add(this.streak);

    this.shadow = new Mesh(buildDecalQuadGeometry(), mats.shadow);
    this.shadow.renderOrder = 1;
    this.root.add(this.shadow);

    this.light = new PointLight(
      entitiesStyle.puck.lightColor,
      entitiesStyle.puck.lightIntensity,
      entitiesStyle.puck.lightDistance,
    );
    this.root.add(this.light);

    activePuckView = this;
  }

  applyStyle(s: EntitiesStyle): void {
    applyPuckStyle(this.u, s);
    this.light.color.set(s.puck.lightColor);
    this.light.intensity = s.puck.lightIntensity * (1 + this.u.heat.value);
    this.light.distance = s.puck.lightDistance;
  }

  hotSwap(mod: PuckModule): void {
    const mats = mod.createPuckMaterials(entitiesStyle);
    for (const [mesh, mat] of [
      [this.body, mats.body],
      [this.streak, mats.streak],
      [this.shadow, mats.shadow],
    ] as const) {
      const old = mesh.material as { dispose(): void };
      mesh.material = mat;
      old.dispose();
    }
    this.u = mats.u;
  }

  /** FX hook: shot-released heat spike (decayed by FxDirector). */
  get uniforms(): PuckUniforms {
    return this.u;
  }

  get pointLight(): PointLight {
    return this.light;
  }

  update(snap: MatchSnapshot): void {
    const s = entitiesStyle;
    const puck = snap.puck;
    const r = tuning.puck.radius;
    const speed = Math.hypot(puck.vel.x, puck.vel.y);
    const lift = puck.mode === "Shot" ? Math.min(s.puck.liftMax, speed / 44) : 0;

    simToWorldInto(this.body.position, puck.pos, r * 0.19 + lift);
    this.light.position.copy(this.body.position);
    this.light.position.y += 0.6;
    this.light.intensity = s.puck.lightIntensity * (1 + this.u.heat.value);

    // Blob shadow grows + fades subtly with lift.
    simToWorldInto(this.shadow.position, puck.pos, 0.013);
    const shadowScale = r * 1.4 * (1 + lift * 0.6);
    this.shadow.scale.setScalar(shadowScale);

    // Motion streak from the previous frame's position.
    if (speed > s.puck.streakMinSpeed && this.hasPrev) {
      const dx = this.prevX - puck.pos.x;
      const dy = this.prevY - puck.pos.y;
      const len = Math.hypot(dx, dy);
      if (len > 0.001) {
        simToWorldInto(this.streak.position, puck.pos, r * 0.2 + lift * 0.5);
        // Tail toward the previous position.
        this.streak.rotation.y = Math.atan2(dy, -dx);
        this.streak.scale.set(r * 1.2, 1, Math.max(len, speed * 0.05));
        this.u.streakAlpha.value = Math.min(0.85, speed / 24);
      }
    } else {
      this.u.streakAlpha.value = 0;
    }
    this.prevX = puck.pos.x;
    this.prevY = puck.pos.y;
    this.hasPrev = true;
  }
}

let activePuckView: PuckView3D | null = null;

if (import.meta.hot) {
  import.meta.hot.accept("../materials/puck.ts", (m) => {
    if (m !== undefined) activePuckView?.hotSwap(m as unknown as PuckModule);
  });
}
